/**
 * "Next up" pointer + category progress — session 80 (dashboard redesign, G7).
 *
 * `computeNextSection` is the SINGLE source for "what's the one highest-priority
 * incomplete thing?" — it backs the Stage-3 dashboard pointer and is built to be
 * reused by the future category-page inline "next" fast-follow. Do NOT fork the
 * priority walk; both surfaces import this.
 *
 * Priority (s80, decisions D-80.x):
 *  1. The first UNMET essential — `nextSteps` is already tier-ordered (T1→T2) and
 *     unmet-only from the Estate Ready Score, so its head is the spine.
 *  2. Once essentials are all met, the first incomplete SECTION in locked IA order
 *     (category `sortOrder` → section `sortOrder`), skipping sections that are
 *     complete (`isSectionComplete`), auto-N/A, in `HIDDEN_SECTIONS`, or have no
 *     completion criterion (no required field — `isSectionComplete` can't score
 *     them, so they are not trackable and never a "next up" target).
 *
 * Section-level tiers do NOT exist in the data (s80 — not added); ordering past the
 * essentials is purely IA category order then section sortOrder.
 */
import { isSectionComplete, sectionProgress, type CompletionEntry, type CompletionField } from "./section-completion";
import type { SectionProgressResult } from "@shared/section-progress";
import { HIDDEN_SECTIONS } from "@shared/constants/section-affordances";
import { SCORING_EXEMPT_SECTIONS } from "@shared/constants/scoring-exempt-sections";
import {
  isSectionAutoNA,
  type SituationFlags,
} from "@shared/constants/section-gating";
import type { SectionOverviewItem } from "@shared/section-overview";
import type { NextStep } from "@shared/readiness";
// s162 (CATEGORY-GATE-OR-ROWS-1): the SHARED row-visibility predicate — never forked.
import { rowIsVisibleUserRecord } from "@shared/lib/row-visibility";

// s159: `SituationFlags` moved alongside the predicate; re-exported for the existing import paths.
export type { SituationFlags };

/** Stable empty default so callers that don't pass a manual-N/A set (and the test
 *  suite's 4-arg calls) keep the prior behavior. */
const EMPTY_MANUAL_NA: ReadonlySet<string> = new Set<string>();

/** Stable empty default for the executor-removed row set (s185, INPUT-ASSEMBLY-1), so callers
 *  that don't pass one keep referential stability and prior behaviour. */
const EMPTY_REMOVED: ReadonlySet<string> = new Set<string>();

export interface NextSection {
  /** The essential's action label, or the section's name. */
  label: string;
  /** Present only for a section pointer — renders the "· {category}" suffix. */
  categoryName?: string;
  /** CTA target — a deep-link `/category/:slug?section=:slug` or a page route. */
  deepLink: string;
  source: "essential" | "section";
  /** Section pointers only — the slug (keys the WHY_LINES copy) and the active
   *  field count (drives the time estimate). Absent for essentials. */
  sectionSlug?: string;
  fieldCount?: number;
}

export interface CategoryProgress {
  categoryId: string;
  categorySlug: string;
  categoryName: string;
  /** Trackable sections that are complete. */
  complete: number;
  /** Trackable sections (≥1 required field, not hidden, not auto-N/A). */
  total: number;
  /** P2 #3: partial-credit progress in [0,1] — the MEAN of each trackable section's
   *  own fraction (a complete section = 1; an incomplete one = filled/total; an
   *  untouched one = 0). Denominator is the section count, so the bar only reaches 1
   *  when every section is complete (which renders as a check, not a bar) — it can
   *  never read "full" while `complete < total`. Additive (callers reading only
   *  `complete`/`total` are unaffected). */
  progressFraction: number;
}

// s159 (LENS-POOLS-1, R3): `isSectionAutoNA` MOVED to `@shared/constants/section-gating` so the
// server-side planner lens can ask the same question (a server module cannot import from client/).
// Re-exported here unchanged, so every existing import path keeps resolving to the one predicate.
export { isSectionAutoNA };

/** A section is "trackable" iff it has a completion criterion (≥1 required field),
 *  is not hidden, is not auto-N/A, and is not manually marked N/A. Untrackable
 *  sections are excluded from the pointer walk and the grid denominator — matching
 *  `isSectionComplete`, which cannot score a section with no required field. */
/**
 * s162 (CATEGORY-GATE-OR-ROWS-1) — section slugs holding ≥1 visible user record.
 *
 * Computed here rather than added to the section-overview API because everything needed is
 * already in hand: `s.fields` gives the section's fieldIds, `entries` is already passed for
 * completion, and `declaresEmptyStarter` already rides on the item (s156 E-fix). Uses the SHARED
 * `rowIsVisibleUserRecord` predicate — the same one the category page, the table renderer and the
 * executor binding resolver apply. Never forked.
 */
function slugsWithVisibleRows(
  sections: SectionOverviewItem[],
  entries: CompletionEntry[],
): ReadonlySet<string> {
  const out = new Set<string>();
  if (entries.length === 0) return out;
  const byField = new Map<string, CompletionEntry[]>();
  for (const e of entries) {
    if (!e.rowId) continue;
    let b = byField.get(e.fieldId);
    if (!b) { b = []; byField.set(e.fieldId, b); }
    b.push(e);
  }
  for (const s of sections) {
    const rows = new Map<string, CompletionEntry[]>();
    for (const f of s.fields) {
      for (const e of byField.get(f.id) ?? []) {
        const rowId = e.rowId as string;
        let r = rows.get(rowId);
        if (!r) { r = []; rows.set(rowId, r); }
        r.push(e);
      }
    }
    for (const [rowId, rowEntries] of Array.from(rows)) {
      if (rowIsVisibleUserRecord(rowId, rowEntries, s.declaresEmptyStarter)) { out.add(s.sectionSlug); break; }
    }
  }
  return out;
}

function isTrackable(
  s: SectionOverviewItem,
  flags: SituationFlags,
  manualNa: ReadonlySet<string>,
  slugsWithRows: ReadonlySet<string>,
): boolean {
  if (HIDDEN_SECTIONS.has(s.sectionSlug)) return false;
  if (manualNa.has(s.sectionSlug)) return false; // manual N/A (s85) — tucked away, never "next up"
  // 🔴 s177 (A2 follow-up): a section with NO completion criterion is not trackable either.
  // D-177.9b made the five password-log sections scoring-exempt — `computeSectionProgress`
  // returns null for them — which broke an invariant this file states 100 lines below
  // ("Trackable sections always yield a non-null progress") and produced THREE visible lies at
  // once: Stage 3 read "26 of 31" (the 5 sat in the denominator and could never join the
  // numerator), the Online Presence tile read 0/5 forever, and NEXT UP pointed at "Devices &
  // Local Access — Do it now", applying completion pressure to the exact sections the ruling
  // had just removed from scoring.
  //
  // Excluding them here rather than special-casing each consumer is the honest reading: if the
  // product will not score a section, it must not chase the planner about it either. The two
  // ideas — "measurable" and "worth pointing at" — were the same set until D-177.9b, and this
  // keeps them the same set.
  if (SCORING_EXEMPT_SECTIONS.has(s.sectionSlug)) return false;
  // s162: flag-OR-rows — a gated section holding real records is trackable even if its flag
  // says false, so a populated section can no longer be silently dropped from the walk.
  if (isSectionAutoNA(s.categorySlug, s.sectionSlug, flags, slugsWithRows)) return false;
  return s.fields.some((f) => !!f.isRequired);
}

function sectionComplete(
  s: SectionOverviewItem,
  entries: CompletionEntry[],
  removedRowIds: ReadonlySet<string> = EMPTY_REMOVED,
): boolean {
  return isSectionComplete(
    { slug: s.sectionSlug, layoutType: s.layoutType ?? undefined, fields: s.fields as CompletionField[], declaresEmptyStarter: s.declaresEmptyStarter },
    entries,
    { removedRowIds },
  );
}

/** Full per-section progress (P2 #3) for an overview item — same shared source as
 *  `sectionComplete`, but returning the fraction the dashboard rolls up. */
function sectionProgressOf(
  s: SectionOverviewItem,
  entries: CompletionEntry[],
  removedRowIds: ReadonlySet<string> = EMPTY_REMOVED,
): SectionProgressResult {
  return sectionProgress(
    { slug: s.sectionSlug, layoutType: s.layoutType ?? undefined, fields: s.fields as CompletionField[], declaresEmptyStarter: s.declaresEmptyStarter },
    entries,
    { removedRowIds },
  );
}

/** IA order (s80): priority tier (1/2/3), then category sortOrder, then section
 *  sortOrder — so a priority-1 section in a later category beats a priority-3
 *  section in an earlier one (the POA / Health & Medical case). */
function byIaOrder(a: SectionOverviewItem, b: SectionOverviewItem): number {
  return (
    (a.priority ?? 3) - (b.priority ?? 3) ||
    a.categorySortOrder - b.categorySortOrder ||
    a.sectionSortOrder - b.sectionSortOrder
  );
}

/**
 * The one highest-priority incomplete thing, or `null` when nothing remains.
 * See the file header for the priority rule.
 */
export function computeNextSection(
  sections: SectionOverviewItem[],
  entries: CompletionEntry[],
  flags: SituationFlags,
  nextSteps: NextStep[] = [],
  manualNa: ReadonlySet<string> = EMPTY_MANUAL_NA,
  // 🔴 s185 (INPUT-ASSEMBLY-1): executor-removed rows, so the pointer never sends a planner to
  // finish a record the executor has already removed.
  removedRowIds: ReadonlySet<string> = EMPTY_REMOVED,
): NextSection | null {
  const slugsWithRows = slugsWithVisibleRows(sections, entries);
  // (1) essentials-first — nextSteps is already tier-ordered + unmet-only.
  if (nextSteps.length > 0) {
    const s = nextSteps[0];
    return { label: s.label, deepLink: s.deepLink, source: "essential" };
  }

  // (2) first incomplete trackable section in IA order.
  for (const s of [...sections].sort(byIaOrder)) {
    if (!isTrackable(s, flags, manualNa, slugsWithRows)) continue;
    if (sectionComplete(s, entries, removedRowIds)) continue;
    return {
      label: s.sectionName,
      categoryName: s.categoryName,
      deepLink: `/category/${s.categorySlug}?section=${s.sectionSlug}`,
      source: "section",
      sectionSlug: s.sectionSlug,
      fieldCount: s.fields.length, // overview fields are active-only (endpoint filters isActive)
    };
  }
  return null;
}

/**
 * Per-category "X of Y sections" over the TRACKABLE sections only (same universe
 * the pointer walks). Y excludes hidden / auto-N/A / no-criterion sections, so the
 * count never implies a section the planner can't actually complete. Structured so
 * a fractional bar (P2 #3) can replace the X/Y count without reshaping callers.
 */
export function summarizeCategoryProgress(
  sections: SectionOverviewItem[],
  entries: CompletionEntry[],
  flags: SituationFlags,
  manualNa: ReadonlySet<string> = EMPTY_MANUAL_NA,
  // 🔴 s185 (INPUT-ASSEMBLY-1): rows the EXECUTOR soft-deleted. The server adapter has filtered
  // these since s145 (`buildSectionProgressInputs` skips `removedRowIds`); this path never did, so
  // the dashboard rollup counted a row the section pill and the server both ignored. That is how
  // `complete@` read "25 of 26" with every section showing a green check.
  removedRowIds: ReadonlySet<string> = EMPTY_REMOVED,
): CategoryProgress[] {
  const slugsWithRows = slugsWithVisibleRows(sections, entries);
  const byCat = new Map<string, CategoryProgress & { sortOrder: number; fractionSum: number }>();
  for (const s of sections) {
    if (!isTrackable(s, flags, manualNa, slugsWithRows)) continue;
    let row = byCat.get(s.categoryId);
    if (!row) {
      row = {
        categoryId: s.categoryId,
        categorySlug: s.categorySlug,
        categoryName: s.categoryName,
        complete: 0,
        total: 0,
        progressFraction: 0,
        fractionSum: 0,
        sortOrder: s.categorySortOrder,
      };
      byCat.set(s.categoryId, row);
    }
    row.total += 1;
    // One shared computation gives both the section-complete tally and each section's
    // own [0,1] fraction (P2 #3). Trackable sections always yield a non-null progress.
    const prog = sectionProgressOf(s, entries, removedRowIds);
    if (prog) {
      row.fractionSum += prog.complete ? 1 : prog.total > 0 ? prog.filled / prog.total : 0;
      if (prog.complete) row.complete += 1;
    }
  }
  return Array.from(byCat.values())
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(({ sortOrder, fractionSum, ...rest }) => ({
      ...rest,
      // Mean of the section fractions — bar reaches 1 only when all are complete.
      progressFraction: rest.total > 0 ? fractionSum / rest.total : 0,
    }));
}
